import { useEffect } from "react";

import { APP_TITLE } from "../../lib/constants";
import {
  alternatePaths,
  langFromPath,
  OG_IMAGE_PATH,
  type MarketingLang,
} from "../../marketing/content/routes";

function upsertMeta(selector: string, attribute: string, value: string, extra?: Record<string, string>) {
  let element = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (!element) {
    const tagName = selector.startsWith("link") ? "link" : "meta";
    element = document.createElement(tagName) as HTMLMetaElement | HTMLLinkElement;
    Object.entries(extra || {}).forEach(([key, attrValue]) => {
      element?.setAttribute(key, attrValue);
    });
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
}

function upsertJsonLd(id: string, data: unknown | null) {
  const existing = document.getElementById(id);
  if (!data) {
    existing?.remove();
    return;
  }
  const script = existing instanceof HTMLScriptElement ? existing : document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.text = JSON.stringify(data);
  if (!existing) document.head.appendChild(script);
}

const HREFLANG_ATTRS = [
  { lang: "fr" as const, hreflang: "fr" },
  { lang: "en" as const, hreflang: "en" },
  { lang: "es" as const, hreflang: "es" },
];

export function usePageMeta(options: {
  title: string;
  description?: string;
  canonicalPath?: string;
  noIndex?: boolean;
  ogType?: "website" | "article";
  ogImage?: string;
  jsonLd?: unknown | unknown[];
  language?: MarketingLang;
}) {
  useEffect(() => {
    const previousTitle = document.title;
    const previousLang = document.documentElement.lang;
    const fullTitle = options.title.includes(APP_TITLE) ? options.title : `${options.title} · ${APP_TITLE}`;
    document.title = fullTitle;
    const description = options.description || "";
    const canonical = options.canonicalPath
      ? `${window.location.origin}${options.canonicalPath}`
      : `${window.location.origin}${window.location.pathname}`;
    const robots = options.noIndex ? "noindex, nofollow" : "index, follow";
    const ogImage = options.ogImage || `${window.location.origin}${OG_IMAGE_PATH}`;
    const language = options.language || langFromPath(window.location.pathname);

    document.documentElement.lang = language;

    upsertMeta('meta[name="description"]', "content", description, { name: "description" });
    upsertMeta('meta[name="robots"]', "content", robots, { name: "robots" });
    upsertMeta('link[rel="canonical"]', "href", canonical, { rel: "canonical" });
    upsertMeta('meta[property="og:title"]', "content", fullTitle, { property: "og:title" });
    upsertMeta('meta[property="og:description"]', "content", description, { property: "og:description" });
    upsertMeta('meta[property="og:url"]', "content", canonical, { property: "og:url" });
    upsertMeta('meta[property="og:type"]', "content", options.ogType || "website", { property: "og:type" });
    upsertMeta('meta[property="og:image"]', "content", ogImage, { property: "og:image" });
    upsertMeta('meta[property="og:locale"]', "content", language === "en" ? "en_US" : language === "es" ? "es_ES" : "fr_FR", {
      property: "og:locale",
    });
    upsertMeta('meta[property="og:site_name"]', "content", APP_TITLE, { property: "og:site_name" });
    upsertMeta('meta[name="twitter:card"]', "content", "summary_large_image", { name: "twitter:card" });
    upsertMeta('meta[name="twitter:title"]', "content", fullTitle, { name: "twitter:title" });
    upsertMeta('meta[name="twitter:description"]', "content", description, { name: "twitter:description" });
    upsertMeta('meta[name="twitter:image"]', "content", ogImage, { name: "twitter:image" });

    const alternates = alternatePaths(options.canonicalPath || window.location.pathname);
    HREFLANG_ATTRS.forEach(({ lang, hreflang }) => {
      upsertMeta(`link[rel="alternate"][hreflang="${hreflang}"]`, "href", `${window.location.origin}${alternates[lang]}`, {
        rel: "alternate",
        hreflang,
      });
    });
    upsertMeta('link[rel="alternate"][hreflang="x-default"]', "href", `${window.location.origin}${alternates.fr}`, {
      rel: "alternate",
      hreflang: "x-default",
    });

    const payloads = Array.isArray(options.jsonLd) ? options.jsonLd : options.jsonLd ? [options.jsonLd] : [];
    payloads.forEach((payload, index) => upsertJsonLd(`ownerflow-jsonld-${index}`, payload));
    for (let index = payloads.length; index < 4; index += 1) {
      upsertJsonLd(`ownerflow-jsonld-${index}`, null);
    }

    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLang;
    };
  }, [
    options.title,
    options.description,
    options.canonicalPath,
    options.noIndex,
    options.ogType,
    options.ogImage,
    options.language,
    JSON.stringify(options.jsonLd ?? null),
  ]);
}
